import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";
import avatar1 from "../../../images/avatar/img01.jpg";
import avatar2 from "../../../images/avatar/img02.jpg";
import avatar3 from "../../../images/avatar/img03.jpg";
import avatar4 from "../../../images/avatar/img04.jpg";

interface Testimonial {
  id: number;
  name: string;
  company: string;
  rating: number;
  review: string;
  avatar: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Vikram Patel",
    company: "LogiTrack Systems",
    rating: 5,
    review:
      "WebTech built a real-time operations dashboard that cut our reporting time by 70%. The insights helped us optimize fleet routes and reduce costs significantly.",
    avatar: avatar1,
  },
  {
    id: 2,
    name: "Sarah Thomas",
    company: "MedCare Hospitals",
    rating: 5,
    review:
      "Their predictive analytics model improved patient flow forecasting accuracy by 85%. Professional team, clean data pipelines, and excellent ongoing support.",
    avatar: avatar2,
  },
  {
    id: 3,
    name: "Arun Krishnan",
    company: "RetailEdge",
    rating: 5,
    review:
      "We finally have a single source of truth for sales data. The Power BI dashboards WebTech delivered are used daily by our entire leadership team.",
    avatar: avatar3,
  },
  {
    id: 4,
    name: "Emily Chen",
    company: "FinServe Global",
    rating: 5,
    review:
      "From ETL setup to ML-based fraud detection, WebTech handled everything end to end. Our false-positive rate dropped by 60% within the first quarter.",
    avatar: avatar4,
  },
];

const StarRating = ({ rating }: { rating: number }) => (
  <div className="da-testimonial__stars" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <svg key={i} viewBox="0 0 20 20" aria-hidden="true">
        <path
          d="M10 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4L10 15.8 4.2 17.9l.9-5.4L1.2 7.7l5.4-.8L10 2Z"
          fill={i < rating ? "#fbbf24" : "rgba(255,255,255,0.15)"}
        />
      </svg>
    ))}
  </div>
);

const DaTestimonials: React.FC = () => {
  const swiperRef = useRef<SwiperType | null>(null);

  return (
    <AnimatedSection as="section" className="da-section da-section--alt" aria-labelledby="da-testimonials-title">
      <div className="da-container">
        <SectionHeader
          eyebrow="Testimonials"
          title="What Our Clients Say"
          text="Trusted by enterprises and startups for data-driven transformation."
        />
      </div>

      <AnimatedSection className="da-testimonials__slider-wrap" delay={0.1}>
        <button
          type="button"
          className="da-testimonials__nav da-testimonials__nav--prev"
          aria-label="Previous testimonial"
          onClick={() => swiperRef.current?.slidePrev()}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <Swiper
          modules={[Autoplay, Pagination]}
          loop
          speed={600}
          spaceBetween={24}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          breakpoints={{
            1200: { slidesPerView: 3 },
            768: { slidesPerView: 2 },
            0: { slidesPerView: 1 },
          }}
          className="da-testimonials__slider"
        >
          {testimonials.map((item) => (
            <SwiperSlide key={item.id}>
              <article className="da-testimonial-card">
                <div className="da-testimonial-card__author">
                  <img src={item.avatar} alt="" className="da-testimonial-card__avatar" />
                  <div>
                    <h3>{item.name}</h3>
                    <span>{item.company}</span>
                  </div>
                </div>
                <StarRating rating={item.rating} />
                <p>&ldquo;{item.review}&rdquo;</p>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          type="button"
          className="da-testimonials__nav da-testimonials__nav--next"
          aria-label="Next testimonial"
          onClick={() => swiperRef.current?.slideNext()}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </AnimatedSection>
    </AnimatedSection>
  );
};

export default DaTestimonials;
